import {
	DocumentSigningRoleType,
	DocumentSigningSecurityLevel,
} from '@dealstack/shared/lib/generated';
import { EidEasySigningMethod } from '@dealstack/shared/lib/common/eideasy/signingMethods';

/** eID Easy "Nordic package" signature methods. */
export const NORDIC_METHODS: readonly EidEasySigningMethod[] = [
	EidEasySigningMethod.SE_BANKID,
	EidEasySigningMethod.NORWEGIAN_BANKID,
	EidEasySigningMethod.MITID,
	EidEasySigningMethod.FREJA,
];

/** Every signing method offered to a (non-witness) signer, in display order. */
const ALL_METHODS: readonly EidEasySigningMethod[] = [
	EidEasySigningMethod.SMART_ID,
	EidEasySigningMethod.FTN,
	EidEasySigningMethod.OTP,
	EidEasySigningMethod.DRAW,
	...NORDIC_METHODS,
];

/** The strong (eID-backed) methods — everything except the handwritten DRAW. */
const STRONG_METHODS: readonly EidEasySigningMethod[] = ALL_METHODS.filter(
	(method) => method !== EidEasySigningMethod.DRAW
);

/** Public signers who attest to someone else's document and carry no eID identifiers. */
export const isAttestingRole = (roleType: DocumentSigningRoleType): boolean =>
	roleType === DocumentSigningRoleType.WITNESS || roleType === DocumentSigningRoleType.CERTIFIER;

/**
 * Methods offered to a signer for their security level and role. Serves every
 * signing surface — the admin `SignersTab`, the investor in-app
 * (`DocumentWorkflowContract`) and the public-link (`PublicSigningPage`) flows.
 *
 * Simple sign (the handwritten `DRAW` method) availability:
 *
 * - **Non-production** (`enableSimpleSign: true`, derived from `!webIsProduction`):
 *   offered to every role at every level, so local/dev signing never requires a
 *   real eID (DC-920). It leads the list as the default option.
 * - **Production** (`enableSimpleSign: false`): offered to any signer whose effective
 *   security level is **BASIC** (the admin's "Enable simple sign" toggle), and withheld
 *   from **ADVANCED** signers (strong methods only).
 *
 * Witnesses and certifiers (DC-825) never get the eID-backed methods (Smart-ID / FTN /
 * Nordic) because we don't collect their supporting identifiers; they get DRAW (when
 * allowed) + OTP.
 *
 * The server-side gates (`eidEasyDocumentSigning.service` for single-doc and
 * `bulk-preparation.service` for bulk) mirror this rule.
 */
export const methodsForSecurityLevel = (
	level: DocumentSigningSecurityLevel,
	roleType: DocumentSigningRoleType,
	{ enableSimpleSign = false }: { enableSimpleSign?: boolean } = {}
): EidEasySigningMethod[] => {
	const drawAllowed = enableSimpleSign || level !== DocumentSigningSecurityLevel.ADVANCED;

	if (isAttestingRole(roleType)) {
		return drawAllowed
			? [EidEasySigningMethod.DRAW, EidEasySigningMethod.OTP]
			: [EidEasySigningMethod.OTP];
	}

	return drawAllowed ? [EidEasySigningMethod.DRAW, ...STRONG_METHODS] : [...STRONG_METHODS];
};
